import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { GALLERY } from "@/data/site";

const item = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const Tile = ({ img, className = "", onOpen }) => (
  <motion.figure
    variants={item}
    onClick={onOpen}
    className={`group relative cursor-pointer overflow-hidden rounded-2xl sm:rounded-3xl ${className}`}
    data-testid="gallery-tile"
  >
    <img
      src={img.url}
      alt={`${img.title} – ${img.cat}`}
      loading="lazy"
      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-90 transition-opacity" />
    <div className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/20 text-white opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
      <ZoomIn className="h-4 w-4" />
    </div>
    <figcaption className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
      <span className="inline-block rounded-full bg-[var(--kilic-green)] px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wider text-white sm:text-xs">
        {img.cat}
      </span>
      <div className="mt-2 font-display text-base font-extrabold text-white sm:text-xl">
        {img.title}
      </div>
    </figcaption>
  </motion.figure>
);

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    (e) => {
      e?.stopPropagation();
      setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length);
    },
    []
  );
  const next = useCallback(
    (e) => {
      e?.stopPropagation();
      setIndex((i) => (i + 1) % GALLERY.length);
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <section id="galerie" className="relative bg-[#fafafa] py-20 md:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[var(--kilic-green-dark)]">
            <Camera className="h-4 w-4" /> Unsere Arbeiten
          </span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tighter text-neutral-900 sm:text-4xl md:text-5xl">
            Echte Projekte aus{" "}
            <span className="text-gradient">Bremen & Umgebung</span>
          </h2>
          <p className="mt-5 text-sm text-neutral-600 sm:text-base md:text-lg">
            Von Fassaden- und Fensterreinigung über Wintergärten bis zur
            Parkett- & Bodenversiegelung – tippen Sie auf ein Bild für die
            Großansicht.
          </p>
        </motion.div>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.05 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"
        >
          {GALLERY.map((img, i) => (
            <Tile
              key={i}
              img={img}
              onOpen={() => setIndex(i)}
              className="aspect-[4/5] sm:aspect-square"
            />
          ))}
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              aria-label="Schließen"
            >
              <X className="h-6 w-6" />
            </button>
            <button
              onClick={prev}
              data-testid="lightbox-prev"
              className="absolute left-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-6"
              aria-label="Vorheriges Bild"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={next}
              data-testid="lightbox-next"
              className="absolute right-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
              aria-label="Nächstes Bild"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <motion.figure
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[85vh] max-w-4xl flex-col items-center"
            >
              <img
                src={GALLERY[index].url}
                alt={GALLERY[index].title}
                className="max-h-[75vh] w-auto rounded-2xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 text-center">
                <span className="inline-block rounded-full bg-[var(--kilic-green)] px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                  {GALLERY[index].cat}
                </span>
                <div className="mt-2 font-display text-lg font-bold text-white">
                  {GALLERY[index].title}
                  <span className="ml-2 text-sm font-normal text-white/50">
                    {index + 1} / {GALLERY.length}
                  </span>
                </div>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
